import {err, ok, ValidationError} from 'framework';
import type {Result, Validator} from 'framework';
import * as z from 'zod';
import type {UpdateSetInput} from '../ports/workout.repository';
import {WorkoutExerciseIdValidator} from './workout-exercise-id.validator';
import {WorkoutIdValidator} from './workout-id.validator';
import {WorkoutSetIdValidator} from './workout-set-id.validator';

export class UpdateSetDataValidator implements Validator<UpdateSetInput> {
  private static readonly workoutId = new WorkoutIdValidator();
  private static readonly workoutExerciseId = new WorkoutExerciseIdValidator();
  private static readonly workoutSetId = new WorkoutSetIdValidator();

  readonly standardSchema = z.strictObject({
    workoutId: UpdateSetDataValidator.workoutId.standardSchema,
    workoutExerciseId: UpdateSetDataValidator.workoutExerciseId.standardSchema,
    setId: UpdateSetDataValidator.workoutSetId.standardSchema,
    weightKg: z.number({error: 'Weight is required.'}).min(0).max(2000).nullable(),
    reps: z.number().int().min(0).max(1000),
    rpe: z.number().min(0).max(10).nullable().optional(),
    isWarmup: z.boolean().optional(),
    isDropset: z.boolean().optional(),
    isFailure: z.boolean().optional(),
    notes: z.string().trim().max(2000).nullable().optional(),
  });

  readonly formSchema = z.strictObject({
    workoutId: UpdateSetDataValidator.workoutId.standardSchema,
    workoutExerciseId: UpdateSetDataValidator.workoutExerciseId.standardSchema,
    setId: UpdateSetDataValidator.workoutSetId.standardSchema,
    weightKg: z.number({error: 'Weight is required.'}).min(0).max(2000).nullable(),
    reps: z.number().int().min(0).max(1000),
    isWarmup: z.boolean(),
  });

  validate(value: unknown): Result<UpdateSetInput, ValidationError> {
    const parsed = this.standardSchema.safeParse(value);
    if (!parsed.success) {
      return err(
        new ValidationError(parsed.error.issues[0]?.message ?? 'Invalid update set data.'),
      );
    }
    return ok(parsed.data);
  }
}
